// src/components/chat/WelcomeScreen.tsx
//
// Minimal centered welcome — replaces the agent-picker grid.
// Approved direction (project_chat_redesign.md): generic LLM with council
// guardrails by default; a hint line below the headline communicates state;
// chips below adapt to the selected persona; persona is picked from the
// sidebar (this component is a passive surface).
//
// v8 cold-start fill (2026-05-08): when no persona is selected, the page
// stops reading as empty by rendering three discoverability sections below
// the composer — generic starter prompts, persona discovery cards, and a
// "pick up where you left off" recent-conversations strip.
//
// Branding unchanged: Roboto, white, brand green.

import React, { useEffect, useRef, useState } from "react";
import { mergeStyleSets } from "@fluentui/react";
import { boroondaraPalette } from "../../theme/boroondaraTheme";
import type { Agent } from "../../types/agent.types";
import SensitiveDataHint from "./SensitiveDataHint";

interface Props {
  agents: Agent[];
  selectedAgent: Agent | null;
  onSelectAgent: (agent: Agent) => void;
  onSendPrompt: (text: string) => void;
  // Cold-start prompt → routes to a specific persona AND sends in one click.
  // Implementation lives in UnifiedChat (handleSend with agent override) so
  // the request uses the right persona without depending on stale state.
  onSendPromptWithAgent?: (text: string, agentId: string) => void;
  isThinking: boolean;
  // Whether the selected agent honors the user's finance hierarchy scope
  agentHonorsHierarchy?: (agentId: string) => boolean;
  // Human-readable scope summary for the selected hierarchy persona
  // (e.g., "Restricted to Customer & Transformation"). Hidden when null.
  financeScopeSummary?: string | null;
  // Tier resolver used by the inline persona dropdown to group agents
  getAgentTier?: (agentId: string) => "public" | "governed";
}

// Starter prompts come from agent.exampleQueries (defined in agentsRoutes.js).
// These are the production-validated council-relevant questions that were
// shipped with the original agent registry — long-tail, accurate, tested.
// Show up to 6 (the agent registry currently provides 6 per persona).
const MAX_PROMPTS = 6;

// Cold-start prompts shown when no persona is selected. Each prompt declares
// its target persona so a single click can scope the answer correctly. Only
// prompts whose target persona is actually available to the user render.
const STARTER_PROMPTS: Array<{ text: string; agentId: string }> = [
  { text: "Population trends for Boroondara since 2016", agentId: "abs" },
  { text: "YTD operating result vs budget", agentId: "finance" },
  { text: "Open work orders aged over 30 days", agentId: "assets" },
  { text: "Top 10 suppliers by spend last financial year", agentId: "finance" },
  { text: "Households with no internet by suburb", agentId: "abs" },
  { text: "Asset condition by category", agentId: "assets" },
];

const styles = mergeStyleSets({
  // Centered layout — status quo when persona is selected (small content).
  wrap: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 24px",
  },
  // Top-aligned layout — used for cold start when discoverability sections
  // fill the page below the composer. Keeps the composer near the top so
  // the page reads as "ready" rather than "lonely middle".
  wrapTop: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "48px 24px 40px",
  },
  inner: {
    width: "100%",
    maxWidth: 760,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  heading: {
    fontSize: 28,
    fontWeight: 500,
    color: boroondaraPalette.text,
    letterSpacing: "-0.01em",
    textAlign: "center" as const,
    margin: "0 0 14px",
  },
  hint: {
    fontSize: 12.5,
    color: boroondaraPalette.text3,
    textAlign: "center" as const,
    margin: "0 0 22px",
    minHeight: 18,
    maxWidth: 540,
  } as any,
  hintStrong: { color: boroondaraPalette.text2, fontWeight: 500 },

  composeWrap: {
    width: "100%",
    background: "#FFFFFF",
    border: `1px solid #D1D5DB`,
    borderRadius: 24,
    padding: "4px 6px 4px 16px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "border-color 0.15s, box-shadow 0.15s",
    selectors: {
      ":focus-within": {
        borderColor: boroondaraPalette.primary,
        boxShadow: `0 0 0 3px rgba(46,135,61,0.10)`,
      },
    },
  } as any,
  plus: {
    width: 32,
    height: 32,
    flexShrink: 0,
    background: "transparent",
    border: 0,
    borderRadius: 50,
    color: boroondaraPalette.text2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    transition: "background 0.12s",
    selectors: { ":hover": { background: "#f8f9fa", color: boroondaraPalette.text } },
  },
  ta: {
    flex: 1,
    border: 0,
    outline: 0,
    background: "transparent",
    fontFamily: "inherit",
    fontSize: 14.5,
    lineHeight: 1.5,
    color: boroondaraPalette.text,
    resize: "none" as const,
    padding: "12px 4px",
    minHeight: 24,
    maxHeight: 200,
  },
  right: { display: "flex", alignItems: "center", gap: 6, paddingRight: 4 },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    background: "#f8f9fa",
    border: `1px solid #E5E7EB`,
    borderRadius: 999,
    fontSize: 12,
    color: boroondaraPalette.text2,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "border-color 0.12s, color 0.12s",
    selectors: { ":hover": { borderColor: "#D1D5DB", color: boroondaraPalette.text } },
  },
  pillDot: {
    width: 6,
    height: 6,
    borderRadius: 50,
    background: boroondaraPalette.primary,
    boxSizing: "border-box" as const,
  },
  pillDotEmpty: {
    background: "transparent",
    border: `1.5px solid #9CA3AF`,
  },
  pillCaret: { fontSize: 9, color: boroondaraPalette.text3 },

  // ── Inline persona-switch dropdown anchored to the pill ──
  pillWrap: { position: "relative" as const },
  dropdown: {
    position: "absolute" as const,
    bottom: "calc(100% + 6px)",
    right: 0,
    minWidth: 240,
    background: "#FFFFFF",
    border: `1px solid #D1D5DB`,
    borderRadius: 8,
    boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
    zIndex: 30,
    padding: 4,
  },
  ddSection: { padding: "4px 0" },
  ddSectionLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    color: boroondaraPalette.text3,
    padding: "4px 10px",
  },
  ddItem: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    width: "100%",
    padding: "6px 10px",
    borderRadius: 5,
    background: "transparent",
    border: 0,
    color: boroondaraPalette.text,
    fontFamily: "inherit",
    fontSize: 13,
    cursor: "pointer",
    textAlign: "left" as const,
    transition: "background 0.12s",
    selectors: { ":hover": { background: "#f8f9fa" } },
  },
  ddItemSelected: {
    background: "#f3fcf3",
    selectors: { ":hover": { background: "#f3fcf3" } },
  },
  ddDot: {
    width: 7,
    height: 7,
    borderRadius: 50,
    border: `1.5px solid #9CA3AF`,
    background: "transparent",
    flexShrink: 0,
    boxSizing: "border-box" as const,
  },
  ddDotSelected: {
    border: `1.5px solid ${boroondaraPalette.primary}`,
    background: boroondaraPalette.primary,
  },
  ddName: { flex: 1 },
  ddMarker: {
    fontFamily: "ui-monospace, Menlo, monospace",
    fontSize: 9.5,
    color: "#9CA3AF",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
  },
  ddDivider: {
    height: 1,
    background: "#EDEDED",
    margin: "4px 0",
  },
  send: {
    width: 34,
    height: 34,
    flexShrink: 0,
    background: boroondaraPalette.primary,
    color: "#FFFFFF",
    border: 0,
    borderRadius: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 16,
    transition: "background 0.12s, transform 0.1s",
    selectors: {
      ":hover": { background: boroondaraPalette.primaryHover },
      ":active": { transform: "translateY(1px)" },
      ":disabled": { background: "#9CA3AF", cursor: "default" },
    },
  } as any,

  // Long-tail prompts need cards, not pills — 2-column grid lets each
  // wrap to 2 lines comfortably. At narrow widths drops to single column.
  chips: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
    gap: 8,
    width: "100%",
    "@media (max-width: 640px)": {
      gridTemplateColumns: "1fr",
    },
  } as any,
  chip: {
    display: "block",
    width: "100%",
    textAlign: "left" as const,
    padding: "11px 14px",
    background: "#FFFFFF",
    border: `1px solid #E5E7EB`,
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: 13,
    lineHeight: "1.4",
    color: boroondaraPalette.text,
    cursor: "pointer",
    transition: "border-color 0.12s, background 0.12s, box-shadow 0.12s",
    selectors: {
      ":hover": {
        borderColor: boroondaraPalette.primary,
        background: "#f3fcf3",
        boxShadow: "0 1px 2px rgba(46,135,61,0.06)",
      },
      ":disabled": { opacity: 0.6, cursor: "default" },
    },
  } as any,

  // ── v8 cold-start sections ──
  section: {
    width: "100%",
    marginTop: 32,
  },
  sectionHead: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    fontSize: 11.5,
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    color: boroondaraPalette.text3,
    marginBottom: 12,
  },
  sectionHeadSub: {
    fontSize: 11,
    fontWeight: 400,
    textTransform: "none" as const,
    letterSpacing: 0,
    color: "#9CA3AF",
  },

  // Starter prompts — same visual language as existing chips with a
  // smaller domain hint underneath. 2-column grid, single-column on narrow.
  promptGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
    gap: 8,
    width: "100%",
    "@media (max-width: 640px)": {
      gridTemplateColumns: "1fr",
    },
  } as any,
  promptCard: {
    display: "block",
    width: "100%",
    textAlign: "left" as const,
    padding: "12px 14px",
    background: "#FFFFFF",
    border: `1px solid #E5E7EB`,
    borderRadius: 10,
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "border-color 0.12s, background 0.12s, box-shadow 0.12s",
    selectors: {
      ":hover": {
        borderColor: boroondaraPalette.primary,
        background: "#f3fcf3",
        boxShadow: "0 1px 2px rgba(46,135,61,0.06)",
      },
      ":disabled": { opacity: 0.6, cursor: "default" },
    },
  } as any,
  promptCardText: {
    fontSize: 13,
    lineHeight: "1.4",
    color: boroondaraPalette.text,
  },
  promptCardDomain: {
    marginTop: 4,
    fontSize: 10.5,
    color: boroondaraPalette.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
  },

});

export default function WelcomeScreen({
  agents,
  selectedAgent,
  onSelectAgent,
  onSendPrompt,
  onSendPromptWithAgent,
  isThinking,
  agentHonorsHierarchy,
  financeScopeSummary,
  getAgentTier,
}: Props) {
  const [text, setText] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const pillWrapRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on outside click / Escape
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!pillWrapRef.current) return;
      if (!pillWrapRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);

  // Resolve tier for grouping in the dropdown
  const tierOf = (a: Agent): "public" | "governed" => {
    if (getAgentTier) return getAgentTier(a.id);
    const fromAgent = (a as Agent & { tier?: string }).tier;
    return fromAgent === "public" ? "public" : "governed";
  };
  const honorsHierarchyOf = (a: Agent): boolean => {
    return !!(agentHonorsHierarchy && agentHonorsHierarchy(a.id));
  };
  const publicAgents = agents.filter((a) => tierOf(a) === "public");
  const governedAgents = agents.filter((a) => tierOf(a) === "governed");

  const honors = !!(selectedAgent && agentHonorsHierarchy && agentHonorsHierarchy(selectedAgent.id));
  const placeholder = selectedAgent
    ? `Ask ${selectedAgent.name} — plain English.`
    : "Ask anything across council data.";
  const pillLabel = selectedAgent ? selectedAgent.name : "Choose a persona";

  // Pull council-relevant starter prompts from the agent registry itself.
  // These are the same exampleQueries that the chat API returns post-message
  // as follow-up suggestions — same source of truth = same wording = consistent.
  const chips = selectedAgent
    ? (selectedAgent.exampleQueries ?? []).slice(0, MAX_PROMPTS)
    : [];

  // v8 cold-start: only show discoverability sections when no persona is
  // selected. Once a persona is chosen the existing chips take over.
  const showColdStart = !selectedAgent;
  const availableAgentIds = new Set(agents.map((a) => a.id));
  const coldStartPrompts = STARTER_PROMPTS.filter((p) => availableAgentIds.has(p.agentId));

  const handleSend = () => {
    const t = text.trim();
    if (!t || isThinking) return;
    onSendPrompt(t);
    setText("");
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStarterPromptClick = (text: string, agentId: string) => {
    if (isThinking) return;
    if (onSendPromptWithAgent) {
      onSendPromptWithAgent(text, agentId);
      return;
    }
    // Fallback: select persona then send. State timing makes this less
    // reliable than the override path, so onSendPromptWithAgent is preferred.
    const target = agents.find((a) => a.id === agentId);
    if (target) onSelectAgent(target);
    onSendPrompt(text);
  };

  const personaForId = (id: string): Agent | undefined =>
    agents.find((a) => a.id === id);

  return (
    <div className={showColdStart ? styles.wrapTop : styles.wrap}>
      <div className={styles.inner}>
        <h1 className={styles.heading}>What would you like to ask today?</h1>

        {/* Hint line — generic guardrail by default; finance scope when applicable */}
        <div className={styles.hint}>
          {selectedAgent && honors && financeScopeSummary ? (
            <>
              <span className={styles.hintStrong}>Finance scope:</span> {financeScopeSummary}
            </>
          ) : selectedAgent ? null : (
            <>
              <span className={styles.hintStrong}>General mode</span> · Council guardrails on. Pick a persona for data-scoped answers.
            </>
          )}
        </div>

        {/* Compose pill */}
        <div className={styles.composeWrap}>
          <button className={styles.plus} type="button" title="Attach (coming soon)">+</button>
          <textarea
            className={styles.ta}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKey}
            placeholder={placeholder}
            autoFocus
          />
          <div className={styles.right}>
            <div className={styles.pillWrap} ref={pillWrapRef}>
              <button
                className={styles.pill}
                type="button"
                title="Switch persona"
                aria-haspopup="listbox"
                aria-expanded={pickerOpen}
                onClick={() => setPickerOpen((v) => !v)}
              >
                <span className={`${styles.pillDot} ${selectedAgent ? "" : styles.pillDotEmpty}`} />
                <span>{pillLabel}</span>
                <span className={styles.pillCaret}>▾</span>
              </button>
              {pickerOpen && (
                <div className={styles.dropdown} role="listbox">
                  {publicAgents.length > 0 && (
                    <div className={styles.ddSection}>
                      <div className={styles.ddSectionLabel}>Public</div>
                      {publicAgents.map((a) => {
                        const isSel = selectedAgent?.id === a.id;
                        return (
                          <button
                            key={a.id}
                            type="button"
                            className={`${styles.ddItem} ${isSel ? styles.ddItemSelected : ""}`}
                            onClick={() => { onSelectAgent(a); setPickerOpen(false); }}
                          >
                            <span className={`${styles.ddDot} ${isSel ? styles.ddDotSelected : ""}`} />
                            <span className={styles.ddName}>{a.name}</span>
                            {honorsHierarchyOf(a) && (
                              <span className={styles.ddMarker}>scope</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {publicAgents.length > 0 && governedAgents.length > 0 && (
                    <div className={styles.ddDivider} />
                  )}
                  {governedAgents.length > 0 && (
                    <div className={styles.ddSection}>
                      <div className={styles.ddSectionLabel}>Governed</div>
                      {governedAgents.map((a) => {
                        const isSel = selectedAgent?.id === a.id;
                        return (
                          <button
                            key={a.id}
                            type="button"
                            className={`${styles.ddItem} ${isSel ? styles.ddItemSelected : ""}`}
                            onClick={() => { onSelectAgent(a); setPickerOpen(false); }}
                          >
                            <span className={`${styles.ddDot} ${isSel ? styles.ddDotSelected : ""}`} />
                            <span className={styles.ddName}>{a.name}</span>
                            {honorsHierarchyOf(a) && (
                              <span className={styles.ddMarker}>scope</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              className={styles.send}
              type="button"
              onClick={handleSend}
              disabled={!text.trim() || isThinking}
              title="Send"
            >→</button>
          </div>
        </div>

        <SensitiveDataHint style={{ marginTop: 10 }} />

        {/* Persona-specific chips — status quo when persona is selected */}
        {!showColdStart && chips.length > 0 && (
          <div className={styles.chips}>
            {chips.map((c) => (
              <button
                key={c}
                className={styles.chip}
                type="button"
                onClick={() => onSendPrompt(c)}
                disabled={isThinking}
              >{c}</button>
            ))}
          </div>
        )}

        {/* v8 cold-start sections — only when no persona selected */}
        {showColdStart && coldStartPrompts.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHead}>
              Try asking
              <span className={styles.sectionHeadSub}>· starts a chat with the right persona</span>
            </div>
            <div className={styles.promptGrid}>
              {coldStartPrompts.map((p) => {
                const target = personaForId(p.agentId);
                if (!target) return null;
                const tierWord = tierOf(target) === "public" ? "public" : "governed";
                return (
                  <button
                    key={p.text}
                    type="button"
                    className={styles.promptCard}
                    onClick={() => handleStarterPromptClick(p.text, p.agentId)}
                    disabled={isThinking}
                  >
                    <div className={styles.promptCardText}>{p.text}</div>
                    <div className={styles.promptCardDomain}>{target.name} · {tierWord}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
